/**
 * BulkNumberCheck
 * ───────────────
 * Marketing Centre → Customer Builder tool: paste a bulk list of mobile
 * numbers + pick a period (+ optional service category) and see which
 * numbers actually visited the workshop (invoice / worked job card in the
 * window). Non-visitors can be loaded into the audience in one click so
 * the SM can resend reminders to the people who never came in.
 */
import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { PhoneCall, Search, RefreshCw, UserX, CheckCircle2, HelpCircle, Send } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SERVICE_TYPES = [
  { value: 'any', label: 'Any service type' },
  { value: 'service', label: 'Service' },
  { value: 'logbook service', label: 'Logbook Service' },
  { value: 'rwc', label: 'Roadworthy (RWC)' },
  { value: 'tyres', label: 'Tyres' },
  { value: 'aircon', label: 'Aircon' },
  { value: 'brakes', label: 'Brakes' },
  { value: 'other', label: 'Other' },
];

export default function BulkNumberCheck({ onLoadNonVisitors }) {
  const [numbersText, setNumbersText] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [serviceType, setServiceType] = useState('any');
  const [checking, setChecking] = useState(false);
  const [data, setData] = useState(null);
  const [view, setView] = useState('all'); // all | visited | not_visited | not_found

  const runCheck = async () => {
    if (!numbersText.trim()) {
      toast.error('Paste at least one mobile number');
      return;
    }
    setChecking(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/marketing/bulk-number-check`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          numbers_text: numbersText,
          date_from: dateFrom || null,
          date_to: dateTo || null,
          service_type: serviceType,
        }),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.detail || 'Check failed');
      }
      const d = await res.json();
      setData(d);
      setView('all');
    } catch (e) {
      toast.error(e.message || 'Check failed');
    } finally {
      setChecking(false);
    }
  };

  const rows = (data?.results || []).filter(r => {
    if (view === 'visited') return r.visited;
    if (view === 'not_visited') return r.matched && !r.visited;
    if (view === 'not_found') return !r.matched;
    return true;
  });

  const statusBadge = (r) => {
    if (!r.valid) return <Badge className="bg-slate-200 text-slate-600">Invalid number</Badge>;
    if (!r.matched) return <Badge className="bg-slate-100 text-slate-500 border border-slate-300">Not in system</Badge>;
    if (r.visited) return <Badge className="bg-emerald-100 text-emerald-700">Visited</Badge>;
    return <Badge className="bg-rose-100 text-rose-700">Did not visit</Badge>;
  };

  return (
    <Card className="p-6" data-testid="bulk-number-check-card">
      <div className="flex items-center gap-2 mb-1">
        <PhoneCall className="h-5 w-5 text-teal-600" />
        <h2 className="font-semibold text-lg text-slate-900">Bulk Mobile Number Check</h2>
      </div>
      <p className="text-sm text-slate-500 mb-4">
        Paste a list of mobile numbers, pick a period and (optionally) a service category —
        see who actually visited. Load the non-visitors into the audience to resend reminders.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        <div className="lg:col-span-5">
          <Label className="text-xs text-slate-600">Mobile numbers (one per line, or comma-separated)</Label>
          <Textarea
            value={numbersText}
            onChange={(e) => setNumbersText(e.target.value)}
            placeholder={'0400 111 222\n0412 345 678\n+61 400 999 888'}
            rows={5}
            className="mt-1 font-mono text-sm resize-y"
            data-testid="bulk-check-numbers-input"
          />
        </div>
        <div className="lg:col-span-7 grid grid-cols-2 gap-3 content-start">
          <div>
            <Label className="text-xs text-slate-600">Visited from</Label>
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="mt-1"
              data-testid="bulk-check-date-from"
            />
          </div>
          <div>
            <Label className="text-xs text-slate-600">Visited to</Label>
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="mt-1"
              data-testid="bulk-check-date-to"
            />
          </div>
          <div className="col-span-2 sm:col-span-1">
            <Label className="text-xs text-slate-600">Service category</Label>
            <Select value={serviceType} onValueChange={setServiceType}>
              <SelectTrigger className="mt-1" data-testid="bulk-check-service-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SERVICE_TYPES.map(s => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2 sm:col-span-1 flex items-end">
            <Button
              onClick={runCheck}
              disabled={checking}
              className="w-full bg-teal-600 hover:bg-teal-700"
              data-testid="bulk-check-run-btn"
            >
              {checking ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
              Check Numbers
            </Button>
          </div>
        </div>
      </div>

      {data && (
        <div className="mt-5 space-y-4">
          {/* Summary chips — clickable filters */}
          <div className="flex flex-wrap gap-2" data-testid="bulk-check-summary">
            {[
              { key: 'all', label: `All (${data.summary.input_count})`, icon: null, cls: 'border-slate-300 text-slate-700' },
              { key: 'visited', label: `Visited (${data.summary.visited})`, icon: CheckCircle2, cls: 'border-emerald-300 text-emerald-700' },
              { key: 'not_visited', label: `Did not visit (${data.summary.not_visited})`, icon: UserX, cls: 'border-rose-300 text-rose-700' },
              { key: 'not_found', label: `Not in system (${data.summary.not_found + data.summary.invalid})`, icon: HelpCircle, cls: 'border-slate-300 text-slate-500' },
            ].map(chip => (
              <button
                key={chip.key}
                type="button"
                onClick={() => setView(chip.key)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-sm transition-colors ${chip.cls} ${view === chip.key ? 'bg-slate-100 font-semibold ring-1 ring-slate-300' : 'bg-white hover:bg-slate-50'}`}
                data-testid={`bulk-check-filter-${chip.key}`}
              >
                {chip.icon && <chip.icon className="h-3.5 w-3.5" />}
                {chip.label}
              </button>
            ))}
            {data.not_visited_customers?.length > 0 && (
              <Button
                size="sm"
                className="ml-auto bg-rose-600 hover:bg-rose-700"
                onClick={() => onLoadNonVisitors?.(data.not_visited_customers)}
                data-testid="bulk-check-load-nonvisitors-btn"
              >
                <Send className="h-3.5 w-3.5 mr-1.5" />
                Load {data.not_visited_customers.length} non-visitor(s) → send reminder
              </Button>
            )}
          </div>

          {/* Results table */}
          <div className="border border-slate-200 rounded-lg overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Number</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Visits in period</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody data-testid="bulk-check-results-body">
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-sm text-slate-400 py-6">
                      Nothing in this view.
                    </TableCell>
                  </TableRow>
                )}
                {rows.map((r, i) => (
                  <TableRow key={`${r.input}-${i}`} data-testid={`bulk-check-row-${i}`}>
                    <TableCell className="font-mono text-sm whitespace-nowrap">{r.input}</TableCell>
                    <TableCell className="text-sm">
                      {r.customer ? r.customer.display_name : <span className="text-slate-400">—</span>}
                    </TableCell>
                    <TableCell>{statusBadge(r)}</TableCell>
                    <TableCell className="text-xs text-slate-600">
                      {r.visits.length === 0 ? (
                        <span className="text-slate-400">—</span>
                      ) : (
                        <div className="space-y-0.5">
                          {r.visits.slice(0, 3).map((v, j) => (
                            <div key={j} className="flex items-center gap-1.5 flex-wrap">
                              <span className="font-medium">{v.date || '?'}</span>
                              {v.doc_no && <Badge variant="outline" className="text-[10px] px-1 py-0">{v.doc_no}</Badge>}
                              {v.service_type && <span className="text-teal-700 uppercase text-[10px]">{v.service_type}</span>}
                              {v.total != null && <span>${Number(v.total).toFixed(2)}</span>}
                              {v.source === 'job_card' && <span className="text-slate-400 text-[10px]">(job card{v.jc_status ? ` · ${v.jc_status}` : ''})</span>}
                            </div>
                          ))}
                          {r.visits.length > 3 && (
                            <div className="text-[10px] text-slate-400">+{r.visits.length - 3} more visit(s)</div>
                          )}
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </Card>
  );
}
